'use client';

import React, { useState, useEffect } from 'react';
import { Icon, Spinner } from '@/src/components/ui';
import { noBorderStyles } from '@/src/constants/constants';
import { Button } from '@workspace/ui/components/button';
import { Input } from '@workspace/ui/components/input';
import { useProfileProjects } from '@/src/hooks/use-fetch-projects';
import { SearchGameItem } from 'types/src/types';
import { searchSocialGames } from '@/src/actions/social-posts';
import { cn } from '@workspace/ui/lib/utils';

type Project = {
  id: string;
  title: string;
  coverImageUrl: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (gameOrProject: any) => void;
  isGameDev: boolean;
  setSelectedGameOrProject?: (value: any) => void;
  userUniqueLink: string;
};

export const GamePickerModal = ({
  isGameDev,
  open,
  onClose,
  onSelect,
  setSelectedGameOrProject,
  userUniqueLink,
}: Props) => {
  const [query, setQuery] = useState('');
  const [myProjects, setMyProjects] = useState<Project[]>([]);
  const [searchResults, setSearchResults] = useState<SearchGameItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<SearchGameItem | null>(null);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const [loading, setLoading] = useState(false);
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useProfileProjects(userUniqueLink);
  const [hoveredGameId, setHoveredGameId] = useState<number | null>(null);

  useEffect(() => {
    if (!data) return;

    const normalized = data.pages.flatMap((page) =>
      page.projects.map((p: any) => ({
        id: p.id,
        title: p.title,
        coverImageUrl:
          p.coverImageUrl || p.coverImage || p.cover_image_url || null,
      }))
    );

    setMyProjects(normalized);
  }, [data]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!query.trim()) {
        setSearchResults([]);
        return;
      }
      searchGames(query);
    }, 1000);

    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSearchResults([]);
      setHoveredKey(null);
      setHoveredGameId(null);
      setLoading(false);
    }
  }, [open]);

  const searchGames = async (search: string) => {
    setLoading(true);
    try {
      const { data, error } = await searchSocialGames(search, 20);

      if (error || !data) {
        console.error('Search error', error);
        setSearchResults([]);
        return;
      }

      setSearchResults(data.items ?? []);
    } catch (e) {
      console.error('searchGames error', e);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const confirm = () => {
    if (!selectedItem) return;

    if (selectedItem.type === 'project') {
      onSelect({
        type: 'project',
        project: {
          id: String(selectedItem.id),
          title: selectedItem.title,
          coverImageUrl: selectedItem.coverBigUrl,
        },
      });

      setSelectedGameOrProject?.({
        image: selectedItem.coverBigUrl,
        title: selectedItem.title,
      });
    } else {
      onSelect({
        type: 'igdb',
        game: {
          igdbId: selectedItem.igdbId!,
          title: selectedItem.title,
          summary: selectedItem.description ?? '',
          slug: selectedItem.slug ?? '',
          cover: selectedItem.coverBigUrl
            ? {
                big: selectedItem.coverBigUrl,
                thumb: selectedItem.coverThumbUrl,
              }
            : null,
          genres: selectedItem.tags ?? [],
        },
      });

      setSelectedGameOrProject?.({
        image: selectedItem.coverBigUrl ?? '',
        title: selectedItem.title,
      });
    }

    onClose();
  };

  if (!open) return null;

  const hasSelection = !!selectedProject || !!selectedItem;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-lg w-full md:max-w-4xl md:max-h-[90vh] flex flex-col overflow-hidden h-[70vh]">
        <div className="md:px-8 md:py-4 p-2 flex items-center justify-between">
          <h2 className="md:text-2xl! text-lg font-bold!">
            {isGameDev ? 'Link a project' : 'Search Games'}
          </h2>
          <Icon
            name="close"
            className="size-4 cursor-pointer"
            onClick={onClose}
          />
        </div>
        <div className="flex-1 min-h-0 h-full overflow-y-auto">
          {isGameDev && (
            <div className="md:px-20 px-5 pb-6">
              <h3 className="md:text-lg! font-semibold md:mb-4 mb-2">
                My Projects
              </h3>
              <div className="overflow-x-auto flex bg-bg-gray md:p-7 p-2 rounded-2xl border gap-16">
                <div className="flex gap-4 pb-2">
                  {!!myProjects.length ? (
                    myProjects.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => {
                          setSelectedItem({
                            type: 'project',
                            id: Number(p.id) || 0,
                            title: p.title,
                            slug: null,
                            coverBigUrl: p.coverImageUrl ?? null,
                            coverThumbUrl: p.coverImageUrl ?? null,
                            description: null,
                            tags: [],
                            isIgdbGame: false,
                          });
                        }}
                        className={`flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all
                    ${
                      selectedProject?.id === p.id
                        ? 'border-blue-primary scale-105'
                        : 'border-transparent hover:border-gray-300'
                    }`}
                      >
                        <img
                          src={p.coverImageUrl}
                          alt={p.title}
                          className="md:size-40 size-20 object-cover"
                        />
                      </button>
                    ))
                  ) : (
                    <div className="text-gray-500">
                      You have no projects yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-hidden flex flex-col md:px-20 px-5 pb-6 min-h-0 h-full">
            {isGameDev && (
              <h3 className="md:text-lg! font-semibold md:mb-4 mb-2">
                Search Games
              </h3>
            )}

            <div className="flex md:bg-bg-gray md:p-5 rounded-2xl md:border gap-16">
              <div className="flex-shrink-0 w-44 h-64 rounded-lg overflow-hidden md:flex hidden items-center justify-center bg-[#C4C4C4]">
                {selectedItem?.coverBigUrl ? (
                  <img
                    src={selectedItem.coverBigUrl as string}
                    alt="Selected game cover"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Icon name="plus" className="size-7 text-black" />
                )}
              </div>

              <div className="flex flex-col gap-6 flex-1">
                <div className="relative w-full">
                  <div className="border rounded-md flex items-center h-10 w-full bg-white">
                    <div className="bg-gray-100 p-3 overflow-hidden rounded-l-md border-r">
                      <Icon
                        name="magnifying-glass"
                        className="size-3.5 text-gray-400"
                      />
                    </div>
                    <Input
                      type="text"
                      style={noBorderStyles}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search games"
                      className="p-4! text-sm bg-white"
                    />
                  </div>

                  {query.trim().length > 0 && (
                    <div className="absolute left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border max-h-60 overflow-y-auto z-80">
                      {loading && (
                        <div className="py-4 flex justify-center">
                          <Spinner />
                        </div>
                      )}

                      {!loading && searchResults.length === 0 && (
                        <div className="py-3 px-4 text-sm text-gray-500">
                          Nothing found
                        </div>
                      )}

                      {!loading &&
                        searchResults.map((item) => {
                          const key =
                            item.type === 'igdb'
                              ? `igdb:${item.igdbId ?? item.id}`
                              : `project:${item.id}`;

                          const isSelected =
                            selectedItem?.type === item.type &&
                            (item.type === 'igdb'
                              ? selectedItem?.igdbId === item.igdbId
                              : selectedItem?.id === item.id);

                          const isHovered = hoveredKey === key;

                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => setSelectedItem(item)}
                              onMouseEnter={() => setHoveredKey(key)}
                              onMouseLeave={() => setHoveredKey(null)}
                              className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors
          ${isSelected || isHovered ? 'bg-pink-accent/5' : 'hover:bg-gray-50'}`}
                            >
                              {item.coverThumbUrl && (
                                <img
                                  src={item.coverThumbUrl}
                                  alt={item.title}
                                  className={cn(
                                    'w-10 h-14 rounded-md object-cover flex-shrink-0',
                                    { 'size-10': item.type === 'project' }
                                  )}
                                />
                              )}

                              <div className="min-w-0 flex-1">
                                <div
                                  className={`text-sm font-medium line-clamp-1 ${isSelected || isHovered ? 'text-pink-accent' : 'text-gray-900'}`}
                                >
                                  {item.title}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>

                <p className="text-lg mt-10 hidden md:block">
                  Search and add a game to your post.
                  <br />
                  Your post will be displayed on this game's page.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="md:px-8 px-2 py-2 flex justify-end">
          <Button
            onClick={confirm}
            disabled={!hasSelection}
            className="md:h-14 rounded-2xl text-sm!"
          >
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
};
